/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import "./UsersGroups.css";

import React from "react";

import { bindActionCreators, Dispatch } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import Context from "lib/context";
import ServicesContext from "services/ServicesContext";

import { DateTime } from "lib/DateTime";

import { CompanyGroupModel, CompanyUserModel } from "data/models/CompanyModel";

import { CompaniesState, slice as companiesSlice } from "store/company/companies.slice";
import { UsersState, slice as usersSlice } from "store/company/users.slice";
import { GroupsState, slice as groupsSlice } from "store/company/groups.slice";

import { UsersGroupsProps, UsersGroupsState } from "./UsersGroups.types";

import DataGrid, { DataGridHeaderProps } from "components/ThreeKey/DataGrid";
import Group from "./Group/Group";
import User from "./User/User";
import MessageBox from "components/ThreeKey/Dialogs/MessageBox";


class UsersGroups extends React.Component<UsersGroupsProps, UsersGroupsState> {

   static contextType = Context;
   public context!: ServicesContext;

   private groupsHeader: DataGridHeaderProps;
   private usersHeader: DataGridHeaderProps;

   private emptyGroup: CompanyGroupModel = {
      _primary_term: -1,
      _seq_no: -1,
      companyId: "",
      id: "",
      name: "",
      description: ""
   }

   private emptyUser: CompanyUserModel = {
      _primary_term: -1,
      _seq_no: -1,
      companyId: "",
      id: "",
      username: "",
      groups: [],
      role: "company_admin", // !!! set to company_user
      lastInvitation: 0
   }

   constructor(props: UsersGroupsProps) {

      super(props);

      this.state = {
         groupDialogShown: false,
         sameGroupNameExistDialogShown: false,
         confirmGroupDeletionDialogShown: false,
         userDialogShown: false,
         sameUserNameExistDialogShown: false,
         confirmUserDeletionDialogShown: false,
         groups: [],
         users: [],
         groupToEdit: this.emptyGroup,
         userToEdit: this.emptyUser,
      }

      this.groupsHeader = {
         visible: true,
         columns: [
            { caption: "Group name", filterable: false, sortable: false, resizable: true },
            { caption: "Group description", filterable: false, sortable: false, resizable: true }
         ]
      }

      this.usersHeader = {
         visible: true,
         columns: [
            { caption: "E-Mail (User Id)", filterable: false, sortable: false, resizable: true },
            { caption: "Invited", filterable: false, sortable: false, resizable: true},
            { caption: "Company Role", filterable: false, sortable: false, resizable: true },
            { caption: "Group Membership", filterable: false, sortable: false, resizable: true }
         ]
      }

   }

   public componentDidMount(): void {
      if (!this.props.companies.selectedCompany) return;
      this.props.actions.loadGroups({ companyId: this.props.companies.selectedCompany.id });
      this.props.actions.loadUsers({ companyId: this.props.companies.selectedCompany.id });
   }

   public componentWillUnmount(): void {
   }

   public componentDidUpdate(prevProps: UsersGroupsProps, prevState: UsersGroupsState) {

      if (prevProps.groups.groups !== this.props.groups.groups) {

         this.setState({
            groups: [...this.props.groups.groups]
               .sort(
                  (a, b) => {
                     const ac = a.name.toLocaleUpperCase();
                     const bc = b.name.toLocaleUpperCase();
                     if (ac < bc) return -1;
                     if (ac > bc) return 1;
                     return 0;
                  }
               )
               .map(g => {
                  return {
                     key: g.companyId + " " + g.id,
                     data: [ g.name, g.description ]
                  }
               })
         });

      }

      if (prevProps.groups.groups !== this.props.groups.groups || prevProps.users.users !== this.props.users.users) {

         this.setState({
            users: [...this.props.users.users]
               .sort(
                  (a, b) => {
                     const ac = a.username.toLocaleUpperCase();
                     const bc = b.username.toLocaleUpperCase();
                     if (ac < bc) return -1;
                     if (ac > bc) return 1;
                     return 0;
                  }
               )
               .map(u => {

                  return {
                     key: u.companyId + " " + u.id,
                     data: [
                        u.username,
                        u.lastInvitation !== 0 ? `Yes~Last invited on ${DateTime.toYYYYMMDDHHMMSS(u.lastInvitation)}` : "No",
                        u.role === "company_admin" ? "admin" : "user",
                        u.groups
                           .map(ugid => this.props.groups.groups.find(g => g.companyId === u.companyId && g.id === ugid)?.name )
                           .filter(g => g !== undefined)
                           .join(", ")
                     ]
                  }
               })
         });

      }

   }

   public render(): React.ReactNode {

      const groupDialog = this.state.groupDialogShown ? <Group onSave={this.onGroupSave} onClose={this.onGroupClose} data={this.state.groupToEdit} /> : <></>;
      const userDialog = this.state.userDialogShown ? <User onSave={this.onUserSave} onClose={this.onUserClose} data={{ user: this.state.userToEdit, groups: this.props.groups.groups }} /> : <></>;
      const groupsLoading = this.props.groups.groupLoading.length > 0 || this.props.groups.groupsLoading || this.props.groups.groupUpdating.length > 0 || this.props.groups.groupCreating.length > 0 || this.props.groups.groupDeleting.length > 0;
      const usersLoading = this.props.users.userLoading.length > 0 || this.props.users.usersLoading || this.props.users.userUpdating.length > 0 || this.props.users.userCreating.length > 0 || this.props.users.userDeleting.length > 0 || this.props.users.userInviting.length > 0;

      const sameGroupNameExistDialog = this.state.sameGroupNameExistDialogShown ?
         <MessageBox
            caption="Error" message="Another group with the same name exist already!"
            cancelOnEscape={true}
            onOk={() => { this.setState({ sameGroupNameExistDialogShown: false }); }}
         />
      :
         <></>
      ;

      const confirmGroupDeletionDialog = this.state.confirmGroupDeletionDialogShown ?
         <MessageBox
            caption="Confirm"
            message="Are you sure you want to delete selected group?"
            cancelOnEscape={true}
            hasCancel={true}
            onOk={this.deleteSelectedGroup}
            onCancel={() => this.setState({ confirmGroupDeletionDialogShown: false })}
         />
      :
         <></>
      ;

      const sameUserNameExistDialog = this.state.sameUserNameExistDialogShown ?
         <MessageBox
            caption="Error" message="Another user with the same name exist already!"
            cancelOnEscape={true}
            onOk={() => { this.setState({ sameUserNameExistDialogShown: false }); }}
         />
      :
         <></>
      ;

      const confirmUserDeletionDialog = this.state.confirmUserDeletionDialogShown ?
         <MessageBox
            caption="Confirm"
            message="Are you sure you want to delete selected user?"
            cancelOnEscape={true}
            hasCancel={true}
            onOk={this.deleteSelectedUser}
            onCancel={() => this.setState({ confirmUserDeletionDialogShown: false })}
         />
      :
         <></>
      ;

      const selectedGroups = this.props.groups.selectedGroup ? [ this.props.groups.selectedGroup.companyId + " " + this.props.groups.selectedGroup.id ] : undefined;

      return (

         <div className="users-groups">

            <div style={{ position: "relative" }}>
               <h4>Groups</h4>

               <DataGrid
                  componentName="groups"
                  header={this.groupsHeader}
                  data={this.state.groups}
                  selectedItems={selectedGroups}
                  onItemSelectionChanged={this.onGroupSelectionChanged}
                  onItemDblClick={this.onGroupDblClick}
                  isLoading={groupsLoading}

               />

               <div className="buttons">
                  <button onClick={this.onGroupAddClick} disabled={true}>Add</button>
                  &nbsp;&nbsp;
                  <button disabled={this.state.groups.length === 0 || this.props.groups.selectedGroup === undefined || true} onClick={this.onGroupEditClick}>Edit</button>
                  &nbsp;&nbsp;
                  <button disabled={this.state.groups.length === 0 || this.props.groups.selectedGroup === undefined || true} onClick={this.onGroupDeleteClick}>Delete</button>
                  &nbsp;&nbsp;
               </div>

               <div style={{position: "absolute", left: 0, top: 0, width: "100%", height: "100%", backgroundColor: "#FFFFFF80"}}></div>
            </div>

            <div>
               <h4>Users</h4>

               <DataGrid
                  componentName="users"
                  header={this.usersHeader}
                  data={this.state.users}
                  onItemSelectionChanged={this.onUserSelectionChanged}
                  onItemDblClick={this.onUserDblClick}
                  isLoading={usersLoading}
               />

               <div className="buttons">
                  <button onClick={this.onUserAddClick} >Add</button>
                  &nbsp;&nbsp;
                  <button disabled={this.state.users.length === 0 || this.props.users.selectedUser === undefined} onClick={this.onUserEditClick}>Edit</button>
                  &nbsp;&nbsp;
                  <button disabled={this.state.users.length === 0 || this.props.users.selectedUser === undefined || this.context.api.auth?.getAuthenticatedUser() === this.props.users.selectedUser.username} onClick={this.onUserDeleteClick}>Delete</button>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <button disabled={this.state.users.length === 0 || this.props.users.selectedUser === undefined} onClick={this.onUserInviteClick} data-loading={this.props.users.userInviting.length > 0}>Invite</button>
                  &nbsp;&nbsp;
               </div>
            </div>

            {groupDialog}
            {sameGroupNameExistDialog}
            {confirmGroupDeletionDialog}
            {userDialog}
            {sameUserNameExistDialog}
            {confirmUserDeletionDialog}

         </div>

      )

   }

   /** Group */

   private onGroupSelectionChanged = (keys: any[]) => {

      if (keys.length === 0) {
         this.props.actions.selectGroupById({ companyId: "", id: "" })
         return;
      }

      const ids = keys[0].split(" ");
      if (this.props.groups.selectedGroup?.companyId === ids[0] && this.props.groups.selectedGroup?.id === ids[1]) return;

      this.props.actions.selectGroupById({ companyId: ids[0], id: ids[1] });
   }

   private onGroupDblClick = (key: any) => {

      if (!this.props.groups.selectedGroup) return;

      this.setState({
         groupToEdit: this.props.groups.selectedGroup,
         groupDialogShown: true
      });

   }

   private onGroupAddClick = () => {

      this.setState({
         groupToEdit: Object.assign({}, this.emptyGroup, { companyId: this.props.companies.selectedCompany!.id }),
         groupDialogShown: true
      });

   }

   private onGroupEditClick = () => {

      if (!this.props.groups.selectedGroup) return;

      this.setState({
         groupToEdit: this.props.groups.selectedGroup,
         groupDialogShown: true
      });

   }

   private onGroupDeleteClick = () => {
      this.setState({ confirmGroupDeletionDialogShown: true });
   }

   /** User */

   private onUserSelectionChanged = (keys: any[]) => {

      if (keys.length === 0) {
         this.props.actions.selectUserById({ companyId: "", id: "" })
         return;
      }

      const ids = keys[0].split(" ");
      this.props.actions.selectUserById({ companyId: ids[0], id: ids[1] })

   }

   private onUserDblClick = (key: any) => {

      if (!this.props.users.selectedUser) return;

      this.setState({
         userToEdit: this.props.users.selectedUser,
         userDialogShown: true
      });
   }

   private onUserAddClick = () => {

      this.setState({
         userToEdit: Object.assign({}, this.emptyUser, { companyId: this.props.companies.selectedCompany!.id }),
         userDialogShown: true
      });

   }

   private onUserEditClick = () => {

      if (!this.props.users.selectedUser) return;

      this.setState({
         userToEdit: this.props.users.selectedUser,
         userDialogShown: true
      });

   }

   private onUserDeleteClick = () => {
      this.setState({ confirmUserDeletionDialogShown: true });
   }


   private onUserInviteClick = () => {

      if (!this.props.users.selectedUser) return;

      this.props.actions.inviteUser({ user: this.props.users.selectedUser });

   }

   /** Dialog handlers - Groups */

   private onGroupSave = (group: CompanyGroupModel) => {

      if (group.id === "") {

         const grp = this.props.groups.groups.find(g => g.name === group.name && g.companyId === group.companyId);
         if (grp) {
            this.setState({ sameGroupNameExistDialogShown: true });
         } else {
            this.props.actions.createGroup({ group });
            this.setState({ groupDialogShown: false });
         }

      } else {

         const grp = this.props.groups.groups.find(g => g.name === group.name && g.companyId === group.companyId && g.id !== group.id);
         if (grp) {
            this.setState({ sameGroupNameExistDialogShown: true });
         } else {
            this.props.actions.updateGroup({ group });
            this.setState({ groupDialogShown: false });
         }

      }

   }

   private onGroupClose = () => {
      this.setState({ groupDialogShown: false });
   }

   private deleteSelectedGroup = () => {
      this.setState({ confirmGroupDeletionDialogShown: false });
      if (!this.props.groups.selectedGroup) return;
      this.props.actions.deleteGroup({ group: this.props.groups.selectedGroup });
   }

   /** Dialog handlers - Users */

   private onUserSave = (user: CompanyUserModel) => {

      if (user.id === "") {

         const usr = this.props.users.users.find(u => u.username === user.username && u.companyId === user.companyId);
         if (usr) {
            this.setState({ sameUserNameExistDialogShown: true });
         } else {
            this.props.actions.createUser({ user });
            this.setState({ userDialogShown: false });
         }

      } else {

         const usr = this.props.users.users.find(u => u.username === user.username && u.companyId === user.companyId && u.id !== user.id);
         if (usr) {
            this.setState({ sameUserNameExistDialogShown: true });
         } else {
            this.props.actions.updateUser({ user });
            this.setState({ userDialogShown: false });
         }

      }

   }

   private onUserClose = () => {
      this.setState({ userDialogShown: false });
   }

   private deleteSelectedUser = () => {
      this.setState({ confirmUserDeletionDialogShown: false });
      if (!this.props.users.selectedUser) return;
      this.props.actions.deleteUser({ user: this.props.users.selectedUser });
   }

}


const mapStateToProps = (state: {
   companies: CompaniesState,
   users: UsersState,
   groups: GroupsState
}) => {
   return state;
}


const mapDispatchToProps = (dispatch: Dispatch) => {

   return {
      actions: bindActionCreators(
         Object.assign(
            {},
            companiesSlice.actions,
            groupsSlice.actions,
            usersSlice.actions
         ),
         dispatch
      )
   }

}

export default connect(
   mapStateToProps,
   mapDispatchToProps
)(UsersGroups);